import { useState, useEffect } from "react";
import axios from "axios";
import { API } from "../../App";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";
import { Building2, TrendingUp, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { formatPropertyRef } from "./utils";

const AdminAllLeads = () => {
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [fixingRefs, setFixingRefs] = useState(false);

  useEffect(() => {
    fetchLeads();
  }, []);

  const fetchLeads = async () => {
    try {
      const response = await axios.get(`${API}/admin/leads`, { withCredentials: true });
      setLeads(response.data);
    } catch (error) {
      toast.error("Failed to fetch leads");
    } finally {
      setLoading(false);
    }
  };

  const syncLeads = async () => {
    setSyncing(true);
    try {
      const response = await axios.post(`${API}/admin/sync-leads`, {}, { withCredentials: true });
      toast.success(response.data.message);
      fetchLeads();
    } catch (error) {
      toast.error("Failed to sync leads");
    } finally {
      setSyncing(false);
    }
  };

  const fixLeadRefs = async () => {
    setFixingRefs(true);
    try {
      const response = await axios.post(`${API}/admin/fix-lead-refs`, {}, { withCredentials: true });
      toast.success(response.data.message);
      fetchLeads();
    } catch (error) {
      toast.error("Failed to fix lead references");
    } finally {
      setFixingRefs(false);
    }
  };

  if (loading) {
    return <div className="flex justify-center p-8"><Loader2 className="w-8 h-8 animate-spin" /></div>;
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h2 className="text-2xl font-serif">All Leads</h2>
          <p className="text-muted-foreground text-sm">View all leads from all agents. Total: {leads.length}</p>
        </div>
        <div className="flex gap-2">
          <Button onClick={fixLeadRefs} disabled={fixingRefs} variant="outline" size="sm">
            {fixingRefs ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Building2 className="w-4 h-4 mr-2" />}
            Fix Property Refs
          </Button>
          <Button onClick={syncLeads} disabled={syncing} variant="outline" size="sm">
            {syncing ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <TrendingUp className="w-4 h-4 mr-2" />}
            Sync from Inquiries
          </Button>
        </div>
      </div>

      <div className="bg-card border border-border rounded-sm overflow-hidden">
        <div className="grid grid-cols-12 gap-4 p-4 bg-muted text-sm font-medium">
          <div className="col-span-2">Property</div>
          <div className="col-span-2">Lead</div>
          <div className="col-span-3">Contact</div>
          <div className="col-span-2">Status</div>
          <div className="col-span-2">Agent</div>
          <div className="col-span-1">Date</div>
        </div>
        <div className="divide-y divide-border max-h-[500px] overflow-y-auto">
          {leads.map((lead) => (
            <div key={lead.lead_id} className="grid grid-cols-12 gap-4 p-4 items-center hover:bg-muted/50">
              <div className="col-span-2">
                <Badge variant="outline" className="font-mono text-xs bg-primary/5">
                  {formatPropertyRef(lead.property_ref)}
                </Badge>
                {lead.property_title && (
                  <p className="text-xs text-muted-foreground mt-1 truncate" title={lead.property_title}>
                    {lead.property_title}
                  </p>
                )}
              </div>
              <div className="col-span-2 font-medium">{lead.name}</div>
              <div className="col-span-3 text-sm text-muted-foreground">
                <p>{lead.email}</p>
                <p>{lead.phone}</p>
              </div>
              <div className="col-span-2">
                <Badge variant={lead.status === "new" ? "default" : "secondary"}>{lead.status}</Badge>
              </div>
              <div className="col-span-2 text-sm">{lead.agent?.name || "Unassigned"}</div>
              <div className="col-span-1 text-xs text-muted-foreground">
                {lead.created_at ? new Date(lead.created_at).toLocaleDateString() : "-"}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default AdminAllLeads;
